import React, { useState, useEffect } from "react";
import axiosInstance from "../../interceptor/axiosInstance";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import "./Dashboard.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Dashboard = () => {
    const currentDate = new Date();
    const currentMonthIndex = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const [selectedMonth, setSelectedMonth] = useState(currentMonthIndex);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [alertMessage, setAlertMessage] = useState("");

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const years = [currentYear - 2, currentYear - 1, currentYear];

    const fetchData = async (month, year) => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`org/get-stats?month=${month + 1}&year=${year}`);
            setData(response.data);
            checkAlert(response.data, month, year);
            setLoading(false);
        } catch (err) {
            setError("Failed to fetch data.");
            setLoading(false);
        }
    };

    const checkAlert = (data, month, year) => {
        let msg = "";
        if (data.consumedTickets >= data.ticketLimit) msg += `Tickets limit reached for ${months[month]} ${year}. `;
        if (data.consumedChats >= data.chatLimit) msg += `Chats limit reached for ${months[month]} ${year}. `;
        if (data.consumedVirtualChats >= data.virtualChatLimit) msg += `Virtual Chats limit reached for ${months[month]} ${year}. `;
        setAlertMessage(msg);
    };

    const handleMonthChange = (e) => {
        const m = parseInt(e.target.value, 10);
        setSelectedMonth(m);
        fetchData(m, selectedYear);
    };

    const handleYearChange = (e) => {
        const y = parseInt(e.target.value, 10);
        setSelectedYear(y);
        const m = y === currentYear && selectedMonth > currentMonthIndex ? currentMonthIndex : selectedMonth;
        setSelectedMonth(m);
        fetchData(m, y);
    };

    useEffect(() => { fetchData(selectedMonth, selectedYear); }, []);

    const isMonthDisabled = (index) => selectedYear === currentYear && index > currentMonthIndex;

    const getProgressClass = (consumed, limit) => {
        const pct = consumed / limit;
        if (pct >= 0.9) return "danger";
        if (pct >= 0.7) return "warning";
        return "safe";
    };

    const chartOptions = (title, max) => ({
        responsive: true,
        scales: { y: { beginAtZero: true, max: Math.ceil(max * 1.2), grid: { color: 'rgba(0,0,0,0.05)' } } },
        plugins: {
            legend: { display: false },
            title: { display: false },
        },
        elements: { bar: { borderRadius: 6 } }
    });

    if (loading) return (
        <div className="dash-spinner">
            <div className="spinner-ring"></div>
        </div>
    );

    if (error) return <div className="dash-page"><p style={{color: '#bb0403'}}>{error}</p></div>;

    const statCards = [
        {
            icon: "T", iconClass: "blue",
            label: "Tickets",
            consumed: data.consumedTickets, limit: data.ticketLimit,
            chartColors: ["#2563eb", "#bfdbfe"],
        },
        {
            icon: "C", iconClass: "green",
            label: "Live Chats",
            consumed: data.consumedChats, limit: data.chatLimit,
            chartColors: ["#16a34a", "#bbf7d0"],
        },
        {
            icon: "V", iconClass: "red",
            label: "Virtual Chats",
            consumed: data.consumedVirtualChats, limit: data.virtualChatLimit,
            chartColors: ["#bb0403", "#fecaca"],
        },
    ];

    return (
        <div className="dash-page">
            <div className="dash-inner">
                {/* Header */}
                <div className="dash-header">
                    <h1 className="dash-title">
                        Usage <span>Overview</span>
                        <span style={{ fontSize: '16px', color: '#475569', fontWeight: 500, marginLeft: '12px' }}>
                            {months[selectedMonth]} {selectedYear}
                        </span>
                    </h1>
                    <div className="dash-controls">
                        <select value={selectedMonth} onChange={handleMonthChange} className="dash-select">
                            {months.map((m, i) => (
                                <option key={i} value={i} disabled={isMonthDisabled(i)}>{m}</option>
                            ))}
                        </select>
                        <select value={selectedYear} onChange={handleYearChange} className="dash-select">
                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                </div>

                {alertMessage && (
                    <div className="dash-alert">
                        {alertMessage}
                    </div>
                )}

                {/* Stat Cards */}
                <div className="dash-cards">
                    {statCards.map((card) => {
                        const pct = Math.min((card.consumed / card.limit) * 100, 100);
                        return (
                            <div className="stat-card" key={card.label}>
                                <div className={`stat-card-icon ${card.iconClass}`}>{card.icon}</div>
                                <div className="stat-card-label">{card.label}</div>
                                <div className="stat-card-value">
                                    {card.consumed} <span>/ {card.limit}</span>
                                </div>
                                <div className="stat-progress-track">
                                    <div
                                        className={`stat-progress-fill ${getProgressClass(card.consumed, card.limit)}`}
                                        style={{ width: `${pct}%` }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Charts */}
                <div className="dash-charts">
                    {statCards.map((card) => (
                        <div className="chart-card" key={`chart-${card.label}`}>
                            <div className="chart-card-title">{card.label} — {months[selectedMonth]} {selectedYear}</div>
                            <Bar
                                data={{
                                    labels: ["Consumed", "Remaining"],
                                    datasets: [{
                                        label: card.label,
                                        data: [card.consumed, card.limit - card.consumed],
                                        backgroundColor: card.chartColors,
                                        borderRadius: 6,
                                    }]
                                }}
                                options={chartOptions(card.label, card.limit)}
                                height={160}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
